import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import html2canvas from "html2canvas";
import useFeatureFlags from "../../hooks/use-feature-flags";
import useFeedbackChat from "./use-feedback-chat";
import { PUDDING_BOT_FLAG } from "./constants";
import puddingIcon from "Assets/images/pudding.png";
import styles from "./feedback-widget.module.scss";

export default function FeedbackWidget() {
  const { isEnabled, loading } = useFeatureFlags();
  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [input, setInput] = useState("");
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    messages,
    isLoading,
    issueCreated,
    images,
    sendMessage,
    addImage,
    removeImage,
    reset,
  } = useFeedbackChat();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  if (loading || !isEnabled(PUDDING_BOT_FLAG)) {
    return null;
  }

  if (isHidden) {
    return null;
  }

  const handleHide = () => {
    setIsHidden(true);
  };

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput("");
    sendMessage(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleScreenshot = async () => {
    setCapturingScreenshot(true);
    setIsOpen(false);

    // Brief delay to let the panel close before capturing
    await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const base64 = dataUrl.replace(/^data:image\/png;base64,/, "");
      addImage(base64);
    } catch {
      // Screenshot failed silently — user can still type their report
    }

    setIsOpen(true);
    setCapturingScreenshot(false);
  };

  const handleReportAnother = () => {
    reset();
  };

  return (
    <>
      <button
        className={styles["trigger-button"]}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Report feedback"
      >
        <img src={puddingIcon} alt="Pudding" className={styles["trigger-icon"]} />
      </button>

      {isOpen && (
        <div className={styles.panel}>
          <div className={styles.header}>
            <h3><img src={puddingIcon} alt="" className={styles["header-icon"]} /> Pudding</h3>
            <div className={styles["header-actions"]}>
              <button
                className={styles["hide-button"]}
                onClick={handleHide}
                title="Hide until the page is reloaded"
              >
                Hide
              </button>
              <button
                className={styles["close-button"]}
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>

          <div className={styles.messages}>
            {messages.length === 0 && (
              <p className={styles.welcome}>
                Hi! I'm Pudding. Tell me about a bug or feature request and I'll
                file it for the team.
              </p>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`${styles.message} ${
                  msg.role === "user"
                    ? styles["message-user"]
                    : styles["message-assistant"]
                }`}
              >
                {msg.role === "assistant" ? (
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                ) : (
                  msg.content
                )}
                {msg.role === "assistant" &&
                  issueCreated &&
                  i === messages.length - 1 && (
                    <a
                      href={issueCreated.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles["issue-link"]}
                    >
                      View issue {issueCreated.id} →
                    </a>
                  )}
              </div>
            ))}
            {isLoading && (
              <div className={styles["typing-indicator"]}>
                Pudding is thinking...
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {issueCreated ? (
            <div className={styles["input-area"]}>
              <button
                className={styles["reset-button"]}
                onClick={handleReportAnother}
              >
                Report another
              </button>
            </div>
          ) : (
            <div className={styles["input-area"]}>
              {images.length > 0 && (
                <div className={styles["pending-images"]}>
                  {images.map((img, i) => (
                    <div key={i} className={styles["pending-image"]}>
                      <img
                        src={`data:image/png;base64,${img}`}
                        alt={`Screenshot ${i + 1}`}
                      />
                      <button
                        className={styles["remove-image"]}
                        onClick={() => removeImage(i)}
                        aria-label="Remove screenshot"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles["input-row"]}>
                <button
                  className={styles["screenshot-button"]}
                  onClick={handleScreenshot}
                  disabled={capturingScreenshot || isLoading}
                  aria-label="Take screenshot"
                  title="Capture screenshot"
                >
                  📷
                </button>
                <textarea
                  ref={inputRef}
                  className={styles["text-input"]}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe the bug or request..."
                  rows={1}
                  disabled={isLoading}
                />
                <button
                  className={styles["send-button"]}
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading}
                  aria-label="Send"
                >
                  ↑
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
